import type { CommunicationData } from '../types/communication-data';

import { apiClient } from './api-client';
import { getHeaders } from './utils';

const getFhirEndpoint = (endpoint: string) => `/fhir${endpoint}`;

/**
 * Fetches a FHIR endpoint through the `/fhir` proxy, as an SWR fetcher.
 *
 * The response is unvalidated JSON, so `TData` is an unchecked claim confined to
 * this boundary rather than validation — see the longer note in `FHIRFetcher`,
 * which this parallels for the `useSWR` call sites.
 *
 * It defaults to {@link CommunicationData} because eleven of the twelve consumers
 * are communication logs already declaring `useSWR<CommunicationData, …>` at their
 * own call site — the default states what those readers had independently agreed
 * on rather than inventing a shape. The twelfth
 * (`common/assignMembersListModal/hooks.ts`) searches `/Patient/` and names
 * `PatientData` itself, which is exactly what the type parameter is for.
 *
 * The `undefined` in the return is real: the guard below resolves WITHOUT calling
 * the API when both `endpoint` and `token` are falsy, and call sites rely on that
 * to defer a fetch until a key is ready.
 *
 * SWR 0.5.7's `Fetcher<Data>` is `(...args: any) => Data | Promise<Data>`, with
 * no room for the `undefined`, so passing `fhirGet` bare fails TS2345. Assert the
 * FETCHER — `fhirGet as () => Promise<CommunicationData>` — which leaves `Data`
 * non-optional and so never disturbs an `onSuccess` body.
 *
 * Reach for `Fetched<T>` (`types/food.ts`) only where the hook has NO `onSuccess`; it
 * makes `data` optional, and most `onSuccess` bodies write `data?.entry?.length > 0`
 * followed by an UNGUARDED `data.entry.…`. That comparison is rejected, and every repair
 * for it changes emitted JavaScript rather than types — dropping `> 0` removes a token
 * from the emit, and inserting `data &&` adds a runtime guard.
 *
 * SUSPECTED DEFECT — REPORTED, NOT FIXED. The guard is `!endpoint && !token`, so
 * it defers only when BOTH are empty. A call with a ready `endpoint` but an empty
 * `token` — which is the state every consumer passes through on first render,
 * before `useAuthContext` resolves — falls through and issues a real request with
 * `Authorization: 'Bearer '`. The sibling fetchers (`FHIRFetcher`, `FoodFetcher`,
 * `LyticsFetcher`, `HooksFetcher`) all guard on the endpoint alone, which suggests
 * `||` was intended. Left exactly as-is: changing it would suppress requests that
 * are currently being made, which is a behaviour change, not a typing one.
 */
export const fhirGet = async <TData = CommunicationData>(
  endpoint: string,
  token: string,
): Promise<TData | undefined> => {
  if (!endpoint && !token) return;

  const data = await apiClient.get<TData>(
    import.meta.env.VITE_API_NAME,
    getFhirEndpoint(endpoint),
    {
      headers: getHeaders(token),
    },
  );

  return data;
};

/**
 * PUTs a FHIR resource through the `/fhir` proxy.
 *
 * `TResource` defaults to `fhir4.Resource`, what FHIR's `update` interaction answers
 * with; every call site updates a `/Communication/` and none reads the reply. The type
 * argument is an assertion, not a check.
 *
 * `body` is `unknown`: it is serialised without being read here.
 */
export const fhirPut = async <TResource = fhir4.Resource>(
  endpoint: string,
  token: string,
  body: unknown,
): Promise<TResource> => {
  const updatedData = await apiClient.put<TResource>(
    'fhir-proxy',
    getFhirEndpoint(endpoint),
    {
      headers: getHeaders(token),
      body,
    },
  );

  return updatedData;
};
